import { ComponentProps, memo, useMemo, useState } from 'react';

import { useIsAllowedPermission } from '@mint-lib/profile-helpers';
import styled, { css } from '@mint-lib/styled';
import { OmniAuthBasic } from '@mint-sdk/api-omni';

import { Box } from '@myn/mui';

import { useUsersDetailsList } from '../../../api/queries/useUsersDetailsList.js';
import { Account, AccountListType } from '../../../types/index.js';
import SkeletonsBox from '../../_atoms/SkeletonsBox/SkeletonsBox.jsx';
import AccountItem from './AccountItem.jsx';
import AddAccountButton from './AddAccountButton.jsx';

const AccountList = ({
  list = [],
  integration,
  logo,
  isListLoading,
  disableAddAccountButton,
}: AccountListType) => {
  const isUserReadOnly = !useIsAllowedPermission('integrations:modify');
  const [filterValue, setFilterValue] = useState<string[]>([]);
  const [sortValue, setSortValue] = useState<{
    type: 'date' | 'alphabet';
    direction: 'asc' | 'desc';
  }>({ type: 'date', direction: 'asc' });
  // get ids of users who updated integrations (without duplication)
  const usersIds = useMemo(() => {
    const ids: string[] = [];
    list.forEach((account) => {
      const connectedByUserId =
        account.connectedByUserId || account.userUpdatedId?.toString();

      if (connectedByUserId && !ids.includes(connectedByUserId)) {
        ids.push(connectedByUserId);
      }
    });

    return ids;
  }, [list]);

  // get first and last name of users who updated the integrations
  const { usersList } = useUsersDetailsList(usersIds);

  const filterOptions = usersList.map(
    (user: { id: number; firstName: string; lastName: string }) => ({
      value: `${user.firstName} ${user.lastName}`,
      label: `${user.firstName} ${user.lastName}`,
    }),
  );

  const mutatedList = useMemo(() => {
    if (!usersList.length) {
      return list;
    }

    // clone 'list' prop and add new key 'connectedBy' what contain first and last name of user who update the integration.
    return list.map((listItem) => {
      const connectedByUserId =
        listItem.connectedByUserId || listItem.userUpdatedId;

      if (!connectedByUserId) {
        return listItem;
      }

      const userInfo = usersList.find((user: { id: number }) => {
        return user.id === Number(connectedByUserId);
      });

      return {
        ...listItem,
        ...(userInfo && {
          connectedBy: `${userInfo.firstName} ${userInfo.lastName}`,
        }),
      };
    });
  }, [list, usersList]);

  const filteredList = useMemo(() => {
    let updatedList = [...mutatedList];
    // filtering cloned list if 'filterValue' is not empty
    if (filterValue.length > 0) {
      updatedList = updatedList.filter((item) => {
        return item.connectedBy && filterValue.includes(item.connectedBy);
      });
    }

    return updatedList;
  }, [filterValue, mutatedList]);

  const sortedList = useMemo(() => {
    const connectedByList = filteredList.filter(
      (listItem) => !!listItem.connectedBy,
    );
    const unUpdatedList = filteredList.filter(
      (listItem) => !listItem.connectedBy,
    );
    // sort cloned filteredList if 'sortValue' is not empty

    if (sortValue.type === 'date') {
      connectedByList.sort((a, b) => {
        const connectedATime = a.connectedAt ?? a.userUpdatedAt;
        const connectedBTime = b.connectedAt ?? b.userUpdatedAt;

        if (!connectedATime || !connectedBTime) {
          return 0;
        }
        const dateA = new Date(connectedATime);
        const dateB = new Date(connectedBTime);
        return sortValue.direction === 'asc'
          ? dateB.getTime() - dateA.getTime()
          : dateA.getTime() - dateB.getTime();
      });
    } else if (sortValue.type === 'alphabet') {
      connectedByList.sort((a, b) => {
        if (a.connectedBy === undefined || b.connectedBy === undefined) {
          return 0;
        }
        return sortValue.direction === 'asc'
          ? a.connectedBy.localeCompare(b.connectedBy)
          : b.connectedBy.localeCompare(a.connectedBy);
      });
    }

    return [...connectedByList, ...unUpdatedList];
  }, [sortValue, filteredList]);

  if (isListLoading) {
    return (
      <Scroll>
        <div
          style={{
            height: '100%',
            width: '100%',
            paddingTop: 18,
            paddingRight: 18,
          }}
        >
          <SkeletonsBox
            skeletonCount={10}
            skeletonClassName="rounded h-15"
            wrapperClassName="flex flex-col gap-2"
          />
        </div>
      </Scroll>
    );
  }

  return (
    <Scroll>
      {isUserReadOnly ? (
        <div className="mt-6 ml-0" />
      ) : (
        <AddAccountButton
          integration={integration}
          logo={logo}
          filter={{ filterValue, setFilterValue, filterOptions }}
          sorting={{ sortValue, setSortValue }}
          disableAddAccountButton={disableAddAccountButton}
        />
      )}

      <Items>
        {sortedList.map(
          (account: Account | OmniAuthBasic.OmniAccountsItemType) => {
            return (
              <AccountItem
                item={{ ...(account as Account), logo }}
                key={account.id}
              />
            );
          },
        )}
      </Items>
    </Scroll>
  );
};

// TODO: migrate to tailwind - styled with options argument (e.g., shouldForwardProp)
const FlexColumn = css`
  display: flex;
  flex-direction: column;
`;

// TODO: migrate to tailwind - styled with options argument (e.g., shouldForwardProp)
const Items = styled(
  ({ gap = 1, pb = 1, ...props }: ComponentProps<typeof Box>) => (
    <Box gap={gap} pb={pb} {...props} />
  ),
)`
  ${FlexColumn}
`;
// TODO: migrate to tailwind - Contains conditional CSS block (interpolation returns full declaration)
const Scroll = styled(Box)`
  ${FlexColumn}
  margin: 0;
  overflow: auto;
  height: 100%;
  width: 438px;
  background-color: ${({ theme }) => theme.palette.ui.secondary};
  box-sizing: border-box;
  ::-webkit-scrollbar {
    width: 0;
  }
`;

export default memo(AccountList);
